// src/user/components/WorkflowDetails.jsx
import React, { useState } from 'react';

/**
 * Reusable component for displaying workflow information
 * Used in AssignedTaskCard and SelfStartWorkflowModal
 * 
 * Props:
 *  - workflow: workflow object with name, instruction, number_of_people, 
 *              expected_work_duration, required_tools, required_parts
 */
export default function WorkflowDetails({ workflow }) {
  const [showDetails, setShowDetails] = useState(false);

  const formatSeconds = (s) => {
    if (!s && s !== 0) return null;
    const mins = Math.round(s / 60);
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h} h ${m} min` : `${h} h`;
  };

  const splitToList = (text) =>
    (text || '')
      .split('\n')
      .map((t) => t.trim())
      .filter(Boolean);

  const tools = splitToList(workflow?.required_tools);
  const parts = splitToList(workflow?.required_parts);
  const duration = formatSeconds(workflow?.expected_work_duration);
  const people = workflow?.number_of_people;

  return (
    <div>
      {/* Title */}
      <h5 className="fw-bold mb-1">{workflow?.name || 'Untitled Workflow'}</h5>
      
      {/* Instruction */}
      {workflow?.instruction && (
        <p className="text-muted mb-1">{workflow.instruction}</p>
      )}

      {/* Meta info */}
      {(people != null || duration) && (
        <div className="text-muted mb-3">
          {people != null && (
            <>
              <i className="bi bi-people me-1" aria-hidden="true"></i>
              {people} {people === 1 ? 'person' : 'people'}
            </>
          )}
          {people != null && duration && <span className="mx-2">•</span>}
          {duration && (
            <>
              <i className="bi bi-stopwatch me-1" aria-hidden="true"></i>
              {duration}
            </>
          )}
        </div>
      )}

      {/* Tools & parts toggle */}
      {(tools.length > 0 || parts.length > 0) && (
        <div className="mb-3">
          <button
            type="button"
            className="btn btn-link p-0"
            onClick={() => setShowDetails((v) => !v)}
          >
            {showDetails ? 'Hide required tools/parts' : 'View required tools/parts'}
          </button>

          {showDetails && (
            <div className="mt-2">
              {tools.length > 0 && (
                <div className="mb-2">
                  <strong>Required tools:</strong>
                  <ul className="mb-0">
                    {tools.map((t, i) => (
                      <li key={`tool-${i}`}>{t}</li>
                    ))}
                  </ul>
                </div>
              )}
              {parts.length > 0 && (
                <div>
                  <strong>Required parts:</strong>
                  <ul className="mb-0">
                    {parts.map((p, i) => (
                      <li key={`part-${i}`}>{p}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


